import type { components } from '@/types/api/v1'
import { useState } from 'react'
import type { MatchDetailResponse } from '../../../../../../api/useSupplierReconciliation/useMatchDetail'

type BolCandidate = components['schemas']['BolCandidate']
type InvoiceCandidate = components['schemas']['InvoiceCandidate']
type ManageMatchItem = BolCandidate | InvoiceCandidate

interface MatchDocumentsState {
  invoices: InvoiceCandidate[]
  bols: BolCandidate[]
}

interface SelectedRow extends Record<string, any> {
  id: string
  invoice_id?: string
  bol_id?: string
}

export function useManageMatchState(matchDetails: MatchDetailResponse, initialMatchDocuments?: MatchDocumentsState) {
  const [currentMatchDocuments, setCurrentMatchDocuments] = useState<MatchDocumentsState>({
    invoices: [],
    bols: [],
  })
  const [removedItems, setRemovedItems] = useState<ManageMatchItem[]>([])
  const [selectedRows, setSelectedRows] = useState<SelectedRow[]>([])

  // Determine intelligent defaults based on match content
  const invoiceDocuments = matchDetails?.document_detail_rows?.filter((row) => row.document_type === 'invoice') || []
  const bolDocuments = matchDetails?.document_detail_rows?.filter((row) => row.document_type === 'bol') || []
  const hasInvoices = invoiceDocuments.length > 0
  const hasBols = bolDocuments.length > 0
  const isUnmatched = matchDetails?.status === 'unmatched'

  // Initialize search type based on existing content - but don't set it immediately
  // Let the main component handle the logic with useEffect
  const [searchType, setSearchType] = useState<'invoice' | 'bol'>('bol')

  // Calculate the primary document ID for this search type
  const getDocumentId = (searchType: 'invoice' | 'bol', currentDocs: MatchDocumentsState) => {
    if (searchType === 'invoice') {
      return currentDocs.bols.length > 0 ? currentDocs.bols[0].bol_id : null
    } else {
      return currentDocs.invoices.length > 0 ? currentDocs.invoices[0].invoice_id : null
    }
  }

  // Add items to the current match
  function addToMatch(items: ManageMatchItem[]) {
    setCurrentMatchDocuments((prev) => {
      const updatedState = { ...prev }

      items.forEach((item) => {
        // Ensure item is an object before checking properties
        if (!item || typeof item !== 'object') {
          console.warn('addToMatch received invalid item:', item)
          return
        }

        if ('invoice_id' in item && item.invoice_id) {
          const exists = updatedState.invoices.some((inv) => inv.invoice_id === item.invoice_id)
          if (!exists) {
            updatedState.invoices.push(item as InvoiceCandidate)
          }
        } else if ('bol_id' in item && item.bol_id) {
          const exists = updatedState.bols.some((bol) => bol.bol_id === item.bol_id)
          if (!exists) {
            updatedState.bols.push(item as BolCandidate)
          }
        }
      })

      return updatedState
    })
  }

  // Remove items from the current match
  function removeFromMatch(items: ManageMatchItem[]) {
    setCurrentMatchDocuments((prev) => {
      const updatedState = { ...prev }

      items.forEach((item) => {
        // Ensure item is an object before checking properties
        if (!item || typeof item !== 'object') {
          console.warn('removeFromMatch received invalid item:', item)
          return
        }

        if ('invoice_id' in item && item.invoice_id) {
          updatedState.invoices = updatedState.invoices.filter((inv) => inv.invoice_id !== item.invoice_id)
        } else if ('bol_id' in item && item.bol_id) {
          updatedState.bols = updatedState.bols.filter((bol) => bol.bol_id !== item.bol_id)
        }
      })

      return updatedState
    })

    // Track removed items for saving - only track valid objects
    const validItems = items.filter((item) => item && typeof item === 'object' && 'original' in item && item.original)
    setRemovedItems((prev) => [...prev, ...validItems])
  }

  // Reset all changes
  function resetChanges() {
    setRemovedItems([])
    setSelectedRows([])
  }

  // Get current document ID for API calls
  function getCurrentDocumentId() {
    return getDocumentId(searchType, currentMatchDocuments)
  }

  // Check if there are unsaved changes
  function hasUnsavedChanges() {
    // Check if items were removed
    if (removedItems.length > 0) {
      return true
    }

    // If we don't have initial documents to compare against, assume no changes
    if (!initialMatchDocuments) {
      return false
    }

    // Check if items were added by comparing current vs initial documents
    const initialInvoiceIds = new Set(initialMatchDocuments.invoices.map((inv) => inv.invoice_id))
    const currentInvoiceIds = new Set(currentMatchDocuments.invoices.map((inv) => inv.invoice_id))

    const initialBolIds = new Set(initialMatchDocuments.bols.map((bol) => bol.bol_id))
    const currentBolIds = new Set(currentMatchDocuments.bols.map((bol) => bol.bol_id))

    // Check if any invoices were added
    for (const id of currentInvoiceIds) {
      if (!initialInvoiceIds.has(id)) {
        return true
      }
    }

    // Check if any BOLs were added
    for (const id of currentBolIds) {
      if (!initialBolIds.has(id)) {
        return true
      }
    }

    return false
  }

  return {
    // State
    currentMatchDocuments,
    setCurrentMatchDocuments,
    removedItems,
    setRemovedItems,
    selectedRows,
    setSelectedRows,
    searchType,
    setSearchType,

    // Computed values
    hasInvoices,
    hasBols,
    isUnmatched,

    // Functions
    addToMatch,
    removeFromMatch,
    resetChanges,
    getCurrentDocumentId,
    hasUnsavedChanges,
    getDocumentId,
  }
}
